import { useEffect, useState, useMemo } from "react";
import { TopBar } from "../components/TopBar";
import { StatusBadge } from "../components/StatusBadge";
import { api, STATUS_LABELS, STATUS_ORDER, formatRelative } from "../lib/api";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  LineChart,
  Line,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import { Activity, TrendingUp, Flame, Clock, CheckCircle2, AlertTriangle, Truck, Package } from "lucide-react";

// Brand-consistent palette aligned with StatusBadge styles
const STATUS_COLOR = {
  awaiting_arrival: "#a1a1aa",
  received: "#3b82f6",
  awaiting_collection: "#eab308",
  collected: "#f97316",
  in_specification: "#8b5cf6",
  ready_for_return: "#d946ef",
  returned: "#ef4444",
  ready_for_dispatch: "#84cc16",
  dispatched: "#10b981",
  delivered: "#047857",
  issue: "#e11d48",
};

const PIPELINE_STATUSES = [
  "awaiting_arrival",
  "received",
  "awaiting_collection",
  "collected",
  "in_specification",
  "ready_for_return",
  "returned",
  "ready_for_dispatch",
  "dispatched",
  "delivered",
];

export default function ReportsPage() {
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/reports/summary"),
      api.get("/bath-records"),
    ]).then(([s, r]) => {
      setSummary(s.data);
      setRecords(r.data);
    });
  }, []);

  // Bar chart data — current pipeline distribution
  const pipelineData = useMemo(() => {
    if (!summary) return [];
    return PIPELINE_STATUSES.map((s) => ({
      key: s,
      name: STATUS_LABELS[s],
      count: summary[s] ?? 0,
      fill: STATUS_COLOR[s],
    }));
  }, [summary]);

  // Pie chart — third party load
  const thirdPartyData = useMemo(() => {
    const buckets = new Map();
    records.forEach((r) => {
      if (r.status === "delivered" || r.status === "dispatched") return; // exclude completed
      const k = r.third_party_name || "Unassigned";
      buckets.set(k, (buckets.get(k) || 0) + 1);
    });
    return Array.from(buckets.entries()).map(([name, value]) => ({ name, value }));
  }, [records]);

  // Throughput — shells in (from Ultra) vs sent out (to Giavani), last 14 days
  const throughputData = useMemo(() => {
    const days = 14;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const rows = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const label = `${d.getDate()}/${d.getMonth() + 1}`;
      const day = d.toISOString().slice(0, 10);
      let received = 0;
      let sent = 0;
      records.forEach((r) => {
        if (r.date_received && r.date_received.slice(0, 10) === day) received++;
        if (r.date_collected && r.date_collected.slice(0, 10) === day) sent++;
      });
      rows.push({ day: label, received, sent });
    }
    return rows;
  }, [records]);

  // Average time per stage (days)
  const stageDurations = useMemo(() => {
    const sums = { collected: [], inSpec: [], dispatched: [], delivered: [] };
    records.forEach((r) => {
      if (r.date_received && r.date_collected) {
        sums.collected.push(diffDays(r.date_received, r.date_collected));
      }
      if (r.date_collected && r.date_returned) {
        sums.inSpec.push(diffDays(r.date_collected, r.date_returned));
      }
      if (r.date_returned && r.date_dispatched) {
        sums.dispatched.push(diffDays(r.date_returned, r.date_dispatched));
      }
      if (r.date_dispatched && r.date_delivered) {
        sums.delivered.push(diffDays(r.date_dispatched, r.date_delivered));
      }
    });
    const avg = (arr) => (arr.length === 0 ? 0 : Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10);
    return [
      { stage: "Receive → Collect", days: avg(sums.collected), n: sums.collected.length },
      { stage: "Collect → Return", days: avg(sums.inSpec), n: sums.inSpec.length },
      { stage: "Return → Dispatch", days: avg(sums.dispatched), n: sums.dispatched.length },
      { stage: "Dispatch → Deliver", days: avg(sums.delivered), n: sums.delivered.length },
    ];
  }, [records]);

  if (!summary) return <div className="min-h-screen bg-zinc-50"><TopBar /><div className="p-8 text-zinc-400">Loading…</div></div>;

  const overdueHint =
    summary.overdue_mode === "past_expected_return"
      ? "past Expected Back date"
      : summary.overdue_mode === "either"
      ? `> ${summary.overdue_days}d or past Expected Back`
      : `> ${summary.overdue_days} days with third party`;

  return (
    <div className="min-h-screen bg-zinc-50">
      <TopBar />
      <main className="mx-auto max-w-[1500px] px-4 sm:px-6 py-6">
        <div className="flex items-end justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-zinc-950 mb-1">
              Reports
            </h1>
            <p className="text-sm text-zinc-600">
              Live snapshots of the bath workflow — pipeline health, throughput, and stage durations.
            </p>
          </div>
        </div>

        {/* Hero tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <Tile icon={Package} label="Total records" value={summary.total} />
          <Tile icon={Activity} label="Received today" value={summary.received_today} />
          <Tile icon={Truck} label="With third party" value={summary.with_third_party} />
          <Tile icon={CheckCircle2} label="Delivered" value={summary.delivered ?? 0} tone="success" />
          <Tile icon={Flame} label="Urgent" value={summary.urgent} tone={summary.urgent > 0 ? "warn" : null} hint="Excludes delivered" />
          <Tile icon={Clock} label="Overdue" value={summary.overdue} tone={summary.overdue > 0 ? "warn" : null} hint={overdueHint} />
        </div>

        {/* Chart grid */}
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          {/* Pipeline distribution */}
          <Panel title="Pipeline by status" subtitle="Current bath count per workflow stage" className="lg:col-span-2" testId="chart-pipeline">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={pipelineData} margin={{ top: 8, right: 8, left: -16, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} tick={{ fontSize: 11, fill: "#52525b" }} stroke="#a1a1aa" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#52525b" }} stroke="#a1a1aa" />
                <Tooltip
                  cursor={{ fill: "rgba(0,0,0,0.04)" }}
                  contentStyle={{ background: "#0a0a0a", border: "none", borderRadius: 6, color: "#fff", fontSize: 12 }}
                  labelStyle={{ color: "#a1a1aa", fontWeight: 600 }}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {pipelineData.map((entry) => (
                    <Cell key={entry.key} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          {/* Third-party load */}
          <Panel title="Active load by third party" subtitle="Excludes dispatched + delivered" testId="chart-thirdparty">
            {thirdPartyData.length === 0 ? (
              <EmptyChart label="No active records right now" />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={thirdPartyData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                    labelLine={false}
                  >
                    {thirdPartyData.map((_, idx) => (
                      <Cell key={idx} fill={["#0a0a0a", "#f97316", "#3b82f6", "#8b5cf6", "#10b981"][idx % 5]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "none", borderRadius: 6, color: "#fff", fontSize: 12 }} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </div>

        <div className="grid lg:grid-cols-2 gap-4 mb-6">
          {/* Throughput */}
          <Panel title="Bath flow — last 14 days" subtitle="Ultra shells in (Received) vs sent to Giavani (Collected)" testId="chart-throughput">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={throughputData} margin={{ top: 8, right: 12, left: -16, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#52525b" }} stroke="#a1a1aa" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#52525b" }} stroke="#a1a1aa" />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "none", borderRadius: 6, color: "#fff", fontSize: 12 }} />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="received" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Received from Ultra" />
                <Line type="monotone" dataKey="sent" stroke="#f97316" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="Sent to Giavani" />
              </LineChart>
            </ResponsiveContainer>
          </Panel>

          {/* Avg stage durations */}
          <Panel title="Average time per stage" subtitle="Days between key date stamps" testId="chart-durations">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={stageDurations} margin={{ top: 8, right: 12, left: -8, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="stage" tick={{ fontSize: 10, fill: "#52525b" }} stroke="#a1a1aa" interval={0} />
                <YAxis allowDecimals={true} tick={{ fontSize: 11, fill: "#52525b" }} stroke="#a1a1aa" />
                <Tooltip
                  formatter={(v, _n, p) => [`${v} days (n=${p.payload.n})`, "Average"]}
                  contentStyle={{ background: "#0a0a0a", border: "none", borderRadius: 6, color: "#fff", fontSize: 12 }}
                />
                <Bar dataKey="days" fill="#0a0a0a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </div>

        {/* Status detail lists (kept, compact) */}
        <div className="mb-2 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-zinc-500" />
          <h2 className="font-heading text-lg font-bold tracking-tight text-zinc-950">Records by status</h2>
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          {STATUS_ORDER.map((s) => {
            const items = records.filter((r) => r.status === s);
            return (
              <div key={s} className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid={`report-bucket-${s}`}>
                <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
                  <StatusBadge status={s} />
                  <span className="text-sm font-mono font-bold text-zinc-950">{items.length}</span>
                </div>
                <ul className="max-h-64 overflow-auto">
                  {items.length === 0 && <li className="px-4 py-3 text-xs text-zinc-400">None</li>}
                  {items.slice(0, 50).map((r) => (
                    <li key={r.id} className="border-t border-zinc-100 first:border-t-0">
                      <Link to={`/bath/${r.id}`} className="block px-4 py-2 hover:bg-zinc-50">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 text-xs">
                              <span className="font-mono font-semibold text-zinc-950">{r.supplier_po}</span>
                              <span className="font-mono text-zinc-500">{r.product_sku}</span>
                            </div>
                            <div className="text-[11px] text-zinc-500 truncate">{r.description}</div>
                          </div>
                          <span className="text-[11px] text-zinc-400 flex-shrink-0">{formatRelative(r.updated_at)}</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

const Tile = ({ label, value, icon: Icon, tone, hint }) => {
  const isWarn = tone === "warn" && value > 0;
  const isSuccess = tone === "success" && value > 0;
  const border = isWarn ? "border-amber-200" : isSuccess ? "border-emerald-200" : "border-zinc-200";
  const valueClr = isWarn ? "text-amber-600" : isSuccess ? "text-emerald-600" : "text-zinc-950";
  return (
    <div className={`bg-white border ${border} rounded-md p-4 shadow-sm`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</span>
        {Icon && <Icon className={`h-3.5 w-3.5 ${isWarn ? "text-amber-500" : isSuccess ? "text-emerald-500" : "text-zinc-300"}`} />}
      </div>
      <div className={`font-heading text-3xl font-extrabold tracking-tighter mt-1 ${valueClr}`}>{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-0.5">{hint}</div>}
    </div>
  );
};

const Panel = ({ title, subtitle, children, className = "", testId }) => (
  <div className={`bg-white border border-zinc-200 rounded-md shadow-sm p-4 ${className}`} data-testid={testId}>
    <div className="mb-3">
      <h3 className="font-heading text-sm font-bold tracking-tight text-zinc-950">{title}</h3>
      {subtitle && <p className="text-[11px] text-zinc-500 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const EmptyChart = ({ label }) => (
  <div className="h-[260px] flex flex-col items-center justify-center text-zinc-400">
    <AlertTriangle className="h-6 w-6 mb-2 text-zinc-300" strokeWidth={1.5} />
    <span className="text-xs">{label}</span>
  </div>
);

function diffDays(aIso, bIso) {
  try {
    const a = new Date(aIso).getTime();
    const b = new Date(bIso).getTime();
    if (Number.isNaN(a) || Number.isNaN(b)) return 0;
    return Math.max(0, (b - a) / 86400000);
  } catch {
    return 0;
  }
}
